import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { Search } from "@/components/search/Search.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import {
  getSettingsGroupLink,
  getSettingsPersonalLinks,
  getSettingsVisibleToEmployee,
} from "@/pages/settings/index/settings.types.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import { Card, CardMini, FontAwesomeIcon, Page, Stack, Text } from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export default function Settings() {
  const { t } = useTranslation();
  const { data: employee } = useEmployeeMe();
  const [searchTerm, setSearchTerm] = useState("");
  useTitle(t("common:settings"));

  const menu = useMemo(() => {
    if (!employee) return [];
    return getSettingsVisibleToEmployee(employee);
  }, [employee]);

  const personalLinks = useMemo(() => {
    if (!employee) return [];
    return getSettingsPersonalLinks(employee);
  }, [employee]);

  const isSearching = searchTerm.trim().length > 0;

  const searchResults = useMemo(() => {
    const searchValue = searchTerm.trim().toLowerCase();
    if (!searchValue) return [];

    return menu
      .map((category) => {
        const items = category.groups.flatMap((group) => {
          const groupMatches = `${group.title} ${group.description}`
            .toLowerCase()
            .includes(searchValue);
          return group.items.filter(
            (item) =>
              groupMatches ||
              `${item.title} ${item.description}`.toLowerCase().includes(searchValue)
          );
        });
        return { title: category.title, items };
      })
      .filter((category) => category.items.length > 0);
  }, [menu, searchTerm]);

  const isSearchEmpty = isSearching && searchResults.length === 0;

  return (
    <Page>
      <Page.Header title={t("common:settings")} />

      <Page.Content>
        <Stack className={"px-16 gap-10"}>
          <Search
            placeholder={t("common:search")}
            value={searchTerm}
            onInput={(e) => setSearchTerm(e.currentTarget.value)}
            clearable={true}
            onClear={() => setSearchTerm("")}
          />
          {isSearchEmpty && <EmptyStateNothingFound />}
          {isSearching &&
            searchResults.map((category) => (
              <Stack key={category.title} gap={"lg"}>
                <Text size={"3xl"} className={"font-semibold"}>
                  {category.title}
                </Text>
                <div className="grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-4">
                  {category.items.map((item) => (
                    <Card
                      key={item.to}
                      icon={item.icon}
                      header={item.title}
                      caption={item.description}
                      to={item.to}
                    />
                  ))}
                </div>
              </Stack>
            ))}
          {!isSearching &&
            menu.map((category, categoryIndex) => {
              const isFirstSection = categoryIndex === 0;
              const [firstGroup, ...restGroups] = category.groups;

              return (
                <Stack key={category.title} gap={"lg"}>
                  <Text size={"3xl"} className={"font-semibold"}>
                    {category.title}
                  </Text>
                  {isFirstSection ? (
                    <Stack gap={"md"}>
                      <div className="flex flex-col md:flex-row gap-4">
                        {employee && (
                          <CardMini
                            size={"fix"}
                            height={"flex"}
                            backgroundUrl={getEmployeeAvatarUrl(employee, "4xl")}
                            title={t("core:settings_account_label")}
                            caption={t("core:settings_account_manage")}
                            to={"/account-settings"}
                          />
                        )}
                        {firstGroup && (
                          <Card
                            className={"flex-1"}
                            icon={firstGroup.icon}
                            header={firstGroup.title}
                            caption={firstGroup.description}
                            to={getSettingsGroupLink(firstGroup)}
                          />
                        )}
                      </div>
                      {restGroups.length > 0 && (
                        <div className="grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                          {restGroups.map((group) => (
                            <Card
                              key={group.key}
                              icon={group.icon}
                              header={group.title}
                              caption={group.description}
                              to={getSettingsGroupLink(group)}
                            />
                          ))}
                        </div>
                      )}
                    </Stack>
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                      {category.groups.map((group) => (
                        <Card
                          key={group.key}
                          icon={group.icon}
                          header={group.title}
                          caption={group.description}
                          to={getSettingsGroupLink(group)}
                        />
                      ))}
                    </div>
                  )}
                </Stack>
              );
            })}
          {!isSearching && personalLinks.length > 0 && (
            <Stack gap={"lg"}>
              <Text size={"3xl"} className={"font-semibold"}>
                {t("core:personal")}
              </Text>
              <div className="grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {personalLinks.map((link) => (
                  <Card
                    key={link.to}
                    icon={link.icon}
                    header={link.title}
                    captionSlot={
                      <Stack direction={"horizontal"} items={"center"} gap={"xs"}>
                        <Text className={"text-base text-secondary"}>{link.description}</Text>
                        <FontAwesomeIcon
                          icon={"fa-light fa-arrow-right"}
                          className={"text-secondary"}
                        />
                      </Stack>
                    }
                    to={link.to}
                    variant="action"
                  />
                ))}
              </div>
            </Stack>
          )}
        </Stack>
      </Page.Content>
    </Page>
  );
}
